import * as THREE from "three";
import {calculateMeshAreaInSquareFeet, GetIdStr, GetShapeOrPath} from './Loader';
import {ft2m, inch2m} from "../../../../../Constants";
import {GetDegVal, GetRadVal, getRoofModuleMeshes, ifRoofDeactivated} from "./Common";
import {CheckInsidePos} from "./RefreshShade";
import {Line2, LineGeometry, LineMaterial} from "three-fatline";
import setbackPathwayService from "../../../../../services/setbackPathwayService";
import {useViewerStore} from "../../../../../store/store";
import {DeleteModule, GetSetPathArr, ModulePlacementStatusSetback, SaveEdgeCount, SetMaxFillForRoofs,} from "./ModuleControl";
import {logger} from "../../../../../services/loggingService";
import {
    Aurora2DCornersOfRoof,
    edgeLine,
    FaceItem,
    Line,
    ModuleMesh,
    PathwayMesh,
    RoofMesh,
    SetbackLineMesh,
    SetbackTypes,
    Vertex3D,
    XYZCoordinates
} from "../../../../../@types/types";
import {isGroundMount} from "./PlaneRoofControls";
import {Viewer} from "../Viewer-Factory/Viewer";
import {AlertType} from "../../../../../@types/enums";

interface fixedSetbackDimension {
    [key: string]: number;
}


export const dePathW: number = 36 * inch2m;

//Default Fixed Setback Dimension
let fixedSetbackDimension: fixedSetbackDimension = {}

let fixedSetbackEdges: SetbackTypes[] = [];

let isFixedRidgeSetback: boolean = false;

export function CheckPos(pos0: Vertex3D, pos1: Vertex3D) {
    return pos0.x === pos1.x && pos0.y === pos1.y && pos0.z === pos1.z;
}

function GetPosArr(lines : Line[]) {
    const posArr : Vertex3D[] = [], line0 = lines[0].points;
    posArr.push(line0[0], line0[1]);
    lines[0].pass = true;
    while (posArr.length < lines.length) {
        const lastPos = posArr[posArr.length - 1];
        let nextPos;
        lines.forEach(line => {
            if (line.pass) return;
            const {points} = line;
            if (CheckPos(points[0], lastPos as Vertex3D)) {
                nextPos = points[1];
                line.pass = true;
            } else if (CheckPos(points[1], lastPos as Vertex3D)) {
                nextPos = points[0];
                line.pass = true;
            }
        });
        if(nextPos)
        posArr.push(nextPos);
    }
    return posArr;
}

export function checkSamePos(posA: Vertex3D, posB: Vertex3D) {
    return posA.x === posB.x && posA.y === posB.y && posA.z === posB.z;
}

export function checkSameLine(lineA: Vertex3D[], lineB: Vertex3D[]) {
    return (checkSamePos(lineA[0], lineB[0]) && checkSamePos(lineA[1], lineB[1])) ||
        (checkSamePos(lineA[0], lineB[1]) && checkSamePos(lineA[1], lineB[0]));
}

function checkExistEdge(edgeArr: edgeLine[], lineN: Vertex3D[]) {
    let flagExist = false;
    edgeArr.forEach(edge => {
        if (flagExist) return;
        const lineE = edge.posArr;
        if (checkSameLine(lineE, lineN)) flagExist = true;
    });
    return flagExist;
}

function getLineMidpointY(points: Vertex3D[]): number {
    return (points[0].y + points[1].y) / 2;
}

function areLinesParallel2D(lineA: Vertex3D[], lineB: Vertex3D[], tol = 0.15): boolean {
    const dx1 = lineA[1].x - lineA[0].x;
    const dz1 = lineA[1].z - lineA[0].z;
    const dx2 = lineB[1].x - lineB[0].x;
    const dz2 = lineB[1].z - lineB[0].z;
    const len1 = Math.hypot(dx1, dz1);
    const len2 = Math.hypot(dx2, dz2);
    if (len1 < 1e-6 || len2 < 1e-6) return false;
    return Math.abs(dx1 * dz2 - dz1 * dx2) / (len1 * len2) < tol;
}

function countFacesSharingLine(faces: FaceItem[], points: Vertex3D[]): number {
    let count = 0;
    for (const face of faces) {
        if (!face.lines) continue;
        if (face.lines.some((line) => line.points && checkSameLine(line.points, points))) {
            count++;
        }
    }
    return count;
}

function inferReplacementEdgeType(faceLines: Line[], ridgeLine: Line): SetbackTypes {
    const points = ridgeLine.points;
    if (!points) return 'EAVE';

    const idx = faceLines.findIndex(
        (line) => line.points && checkSameLine(line.points, points),
    );
    if (idx === -1) return 'EAVE';

    const prev = faceLines[(idx - 1 + faceLines.length) % faceLines.length];
    const next = faceLines[(idx + 1) % faceLines.length];
    const neighborTypes = [prev.type, next.type].filter(
        (type): type is SetbackTypes => !!type && type !== 'RIDGE',
    );

    if (neighborTypes.length === 2 && neighborTypes[0] === neighborTypes[1]) {
        return neighborTypes[0];
    }

    for (const line of faceLines) {
        if (!line.points || line.type === 'RIDGE' || !['EAVE', 'RAKE'].includes(line.type)) {
            continue;
        }
        if (areLinesParallel2D(points, line.points)) {
            return line.type as SetbackTypes;
        }
    }

    const ridgeY = getLineMidpointY(points);
    const eaves = faceLines.filter((line) => line.type === 'EAVE' && line.points);
    if (eaves.length > 0) {
        const eaveY =
            eaves.reduce((sum, line) => sum + getLineMidpointY(line.points!), 0) / eaves.length;
        const rakes = faceLines.filter((line) => line.type === 'RAKE' && line.points);
        const rakeY =
            rakes.length > 0
                ? rakes.reduce((sum, line) => sum + getLineMidpointY(line.points!), 0) / rakes.length
                : ridgeY + 1;

        return Math.abs(ridgeY - eaveY) <= Math.abs(ridgeY - rakeY) ? 'EAVE' : 'RAKE';
    }

    return neighborTypes[0] ?? 'EAVE';
}

function syncEdgeTypeFromLine(edges: edgeLine[], points: Vertex3D[], type: SetbackTypes) {
    const normalizedType = type.toLowerCase();
    edges.forEach((edge) => {
        if (edge.posArr && checkSameLine(edge.posArr, points)) {
            edge.type = normalizedType;
        }
    });
}

/** Ridges belong at interior roof peaks, not on single-face perimeter edges. */
export function fixIncorrectRidgeLines(faces: FaceItem[], edges: edgeLine[]) {
    const allRidgeLines: Line[] = [];
    faces.forEach((face) => {
        face.lines?.forEach((line) => {
            if (line.type === 'RIDGE' && line.points) {
                allRidgeLines.push(line);
            }
        });
    });

    if (allRidgeLines.length === 0) return;

    const validHighestRidges = GetHighestRidges(allRidgeLines);
    const isInHighestRidgeCluster = (line: Line) =>
        validHighestRidges.some(
            (ridge) => ridge.points && line.points && checkSameLine(ridge.points, line.points),
        );

    faces.forEach((face) => {
        if (!face.lines) return;

        face.lines.forEach((line) => {
            if (line.type !== 'RIDGE' || !line.points) return;

            const sharedFaceCount = countFacesSharingLine(faces, line.points);
            const isValidRidge = sharedFaceCount >= 2 && isInHighestRidgeCluster(line);
            if (isValidRidge) return;

            const newType = inferReplacementEdgeType(face.lines, line);
            line.type = newType;
            syncEdgeTypeFromLine(edges, line.points, newType);
        });
    });
}

export async function fetchPathwayDataFromBackend() {

    const self: Viewer = useViewerStore.getState().sceneViewer;

    const salesforceId = sessionStorage.getItem('SalesForceId');

    if (!salesforceId) {
        logger.error('Salesforce ID is missing.');
        return;
    }

    const lines = await fetchSetbackInfo(salesforceId, self);

    if (lines && self.roofs && self.roofs[0]) {
        self.roofs[0].faces.forEach((face: FaceItem) => {
            if (!face.lines) return;

            face.lines.forEach(line => {
                const selApiLine = lines.find(
                    (apiLine: Line) =>
                        apiLine?.type?.toLowerCase() === line?.type?.toLowerCase()
                );
                if (!selApiLine) return;

                const {pathway} = selApiLine;

                // NOTE : possiblePathway maps to detached structured exempt in SF
                line.setback = 36; // setback?parseInt(setback.substring(0, 2)):0;
                line.pathway = line.type !== 'RIDGE' && pathway === true;
            });
        });
    }
}


export async function SetPathway(self: Viewer) {
    const {roofs} = self;

    if (!roofs || !roofs[0] || roofs[0].faces?.[0]?.lines) return;

    const {faces, edges} = roofs[0];


    // build lines into each face
    faces.forEach((face: FaceItem) => {
        const {azimuth, pitch, size, posArr, oriAng, roof_number_from_aurora} = face;
        const lines: Line[] = [];

        posArr.forEach((pos, posIdx) => {
            const idxNext = posIdx === posArr.length - 1 ? 0 : posIdx + 1;
            const posNext = posArr[idxNext];

            const points = [{...pos}, {...posNext}];
            let lineType: any;


            edges.forEach((edge: edgeLine) => {
                if (!checkSameLine(points, edge.posArr) || lineType) return;
                lineType = edge.type;
            });

            const type = lineType?.toUpperCase();
            const lineItem = {
                lineId: GetIdStr(),
                points,
                type,
                streetSide: false,
                enforcedPathway: false,
            };


            lines.push(lineItem);
        });

        // attach computed data back to the original face
        face.lines = lines;
        face.tilt = GetDegVal(pitch);
        face.azimuthDeg = GetDegVal(azimuth);
        face.oriAng = oriAng;
        face.roof_number_from_aurora = roof_number_from_aurora;
        face.size = size;
    });

    // Fix: convert valleys to ridges if they are very close to a ridge (Special Case)
    const allRidgeLines: Line[] = [];
    const allValleyLines: Line[] = [];

    faces.forEach((face: FaceItem) => {
        if (!face.lines) return;
        face.lines.forEach((line: Line) => {
            if (line.type === 'RIDGE') allRidgeLines.push(line);
            if (line.type === 'VALLEY') allValleyLines.push(line);
        });
    });

    allValleyLines.forEach(valleyLine => {
        allRidgeLines.forEach(ridgeLine => {

            if (!ridgeLine.points || !valleyLine.points) {
                logger.error('Invalid ridge or valley line points');
                console.error("Invalid ridge or valley line points");
                return;
            }

            const ridgeMidPoint = {
                x: (ridgeLine.points[0].x + ridgeLine.points[1].x) / 2,
                y: (ridgeLine.points[0].y + ridgeLine.points[1].y) / 2,
                z: (ridgeLine.points[0].z + ridgeLine.points[1].z) / 2,
            };
            const valleyMidPoint = {
                x: (valleyLine.points[0].x + valleyLine.points[1].x) / 2,
                y: (valleyLine.points[0].y + valleyLine.points[1].y) / 2,
                z: (valleyLine.points[0].z + valleyLine.points[1].z) / 2,
            };

            const dis = Math.abs(
                Math.sqrt(
                    Math.pow(ridgeMidPoint.x - valleyMidPoint.x, 2) +
                    Math.pow(ridgeMidPoint.z - valleyMidPoint.z, 2),
                ),
            );

            // Fix ridges incorrectly marked as valleys
            if (dis < 0.5) {
                valleyLine.type = 'RIDGE';
            }
        });
    });

    //TODO: This needs to be fixed to work with the new logic to identify incorrectly marked ridges.
    // fixIncorrectRidgeLines(faces, edges);
    SaveEdgeCount(edges);

    setFaceAndEdgeData(self);

    await fetchPathwayDataFromBackend();
}

// typescript
export async function fetchSetbackInfo(opportunityId: string, self: Viewer): Promise<Line[]> {
    try {
        const res: any = await setbackPathwayService.getSetbackPathway(opportunityId);

        if (!res?.data || !res.data[0]) {
            logger.error('SETBACK/PATHWAY API ERROR');
            if (self) {
                self.viewerAlert({
                    show: true,
                    messageType: AlertType.error,
                    title: 'SETBACK/PATHWAY API ERROR',
                    message: 'Setback Error',
                    isModal: true,
                });
            }
            useViewerStore.setState({aHJ_Township: '', setbackNotes: []});
            return [];
        }

        const {lines = [], ahjTown} = res.data[0] as { lines?: Line[]; ahjTown?: string };
        const notes: string[] = [];
        const groundMountNotes: string[] = [];

        // collect unique notes with guards
        for (const line of lines) {
            const roofNote = line.roofMountSetbackNotes?.toString().trim();
            const note = line.notes?.toString().trim();
            const groundNote = line.groundMountSetbackNotes?.toString().trim();

            if (note && !notes.includes(note)) notes.push(note);
            if (groundNote && !groundMountNotes.includes(groundNote)) groundMountNotes.push(groundNote);
            if (roofNote && !notes.includes(roofNote)) notes.push(roofNote);
        }

        // process fixed setbacks (with guards)
        for (const line of lines) {
            const lineType = line.type ? line.type.toString() : '';

            if (line.isDefault === false) {
                if (lineType.toLowerCase() === 'ridge') {
                    isFixedRidgeSetback = true;
                }

                const key = lineType.toUpperCase() as SetbackTypes;
                fixedSetbackEdges.push(key);
                fixedSetbackDimension[key] = Number(line.setback ?? 0);
            }

            if (line.isDefault === true && lineType.toLowerCase() === 'ridge') {
                const key = lineType.toUpperCase() as SetbackTypes;
                fixedSetbackEdges.push(key);
                fixedSetbackDimension[key] = 36;
            }
        }

        if (fixedSetbackEdges.length > 0 && self) {
            self.FixedsetbackForAllEdges = true;
        }

        useViewerStore.setState({aHJ_Township: ahjTown ?? '', setbackNotes: notes, groundMountNotes});
        return lines;
    } catch (err: any) {
        useViewerStore.setState({aHJ_Township: '', setbackNotes: []});
        logger.error(err);
        if (self) {
            self.viewerAlert({
                show: true,
                messageType: AlertType.error,
                title: 'SETBACK/PATHWAY API ERROR',
                message: err?.message ?? String(err),
                isModal: true,
            });
        }
        return [];
    }
}


export function setFaceAndEdgeData(self: Viewer) {
    if (!self.roofJson) return;
    self.roofs[0].faces.forEach(((face: FaceItem, idx: number) => {
        face.lines.forEach((line: Line) => {
            if (!line.points) return;
            line.points.forEach(pos => {
                const oldZ = pos.z;
                pos.z = pos.y * -1;
                pos.y = oldZ;
                pos.oldY = oldZ;
            });
            if (!checkExistEdge(self.roofs[0].edges, line.points)) {
                self.roofs[0].edges.push({
                    posArr: [...line.points],
                    type: line.type?.toLowerCase(),
                    roofFaceId: face.roofFaceId
                });
            }
        });
        face.buildingName = self.roofs[0].faces[idx].buildingName;
        face.irrDir = {tilt: -face.tilt, azimuth: -face.azimuth};
        face.posArr = GetPosArr(face.lines);
        face.azimuth = GetRadVal(face.azimuth);
        face.pitch = GetRadVal(face.tilt);
        face.holes = null;
        face.modules = face?.moduleArr?.length || 0;
    }));
}


function GetPosPath2D(pos2DArr: Aurora2DCornersOfRoof[], type: string, pathW: number, posIdx: number, idxNext: number) {


    const posFirst = pos2DArr[posIdx], posNext = pos2DArr[idxNext],
        disX = posFirst.x - posNext.x, disZ = posFirst.z - posNext.z;
    const pos02D = pos2DArr[posIdx], pos12D = pos2DArr[idxNext]
    let radH, radV;

    if (type === 'VALLEY') {
        radH = Math.atan(disZ / disX);

    } else {
        radH = Math.atan(disX / disZ);
    }

    if (type === 'VALLEY' || type === 'HIP') {
        radV = radH + Math.PI / 2;
    } else {
        radV = radH
    }


    const deltaX = Math.cos(radV) * pathW, deltaZ = Math.sin(radV) * pathW;
    const pos0d0 = {x: pos02D.x + deltaX, y: 0, z: pos02D.z + deltaZ},
        pos0d1 = {x: pos02D.x - deltaX, y: 0, z: pos02D.z - deltaZ},
        pos1d0 = {x: pos12D.x + deltaX, y: 0, z: pos12D.z + deltaZ},
        pos1d1 = {x: pos12D.x - deltaX, y: 0, z: pos12D.z - deltaZ};
    let flagD0 = false, flagD1 = false, posPath2D;
    const countInterval = 4;
    const intervalX0 = (pos1d0.x - pos0d0.x) / countInterval,
        intervalZ0 = (pos1d0.z - pos0d0.z) / countInterval,
        intervalX1 = (pos1d1.x - pos0d1.x) / countInterval,
        intervalZ1 = (pos1d1.z - pos0d1.z) / countInterval;


    for (let i = 0; i <= countInterval; i++) {
        if (flagD0 || flagD1) continue;
        const middlePos0 = {x: pos0d0.x + intervalX0 * i, z: pos0d0.z + intervalZ0 * i};
        const middlePos1 = {x: pos0d1.x + intervalX1 * i, z: pos0d1.z + intervalZ1 * i};
        if (CheckInsidePos(pos2DArr, middlePos0)) {
            flagD0 = true;
        } else if (CheckInsidePos(pos2DArr, middlePos1)) {
            flagD1 = true;
        }
    }
    if (flagD0) posPath2D = [pos0d0, pos1d0];
    else if (flagD1) posPath2D = [pos0d1, pos1d1];
    // else posPath2D = [];
    return {posPath2D, pos02D, pos12D};
}

function GetPosPath2DForPathway(pos2DArr: Aurora2DCornersOfRoof[], type: string, pathW: number, posIdx: number, idxNext: number) {
    if (!Array.isArray(pos2DArr) || pos2DArr.length < 2) {
        return { posPath2D: undefined, pos02D: undefined, pos12D: undefined };
    }
    const posFirst = pos2DArr[posIdx], posNext = pos2DArr[idxNext];
    if (!posFirst || !posNext) {
        return { posPath2D: undefined, pos02D: undefined, pos12D: undefined };
    }
    const disX = posNext.x - posFirst.x, disZ = posNext.z - posFirst.z;
    const pos02D = pos2DArr[posIdx], pos12D = pos2DArr[idxNext];

    // heading (horizontal) and perpendicular direction (vertical) depending on edge type
    const radH = Math.atan2(disZ, disX);
    let radV;
    if (type === 'VALLEY' || type === 'HIP') {
        radV = radH + Math.PI / 2;
    } else if (type === 'RAKE') {
        radV = radH - Math.PI / 2;
    } else {
        radV = radH;
    }

    const deltaX = Math.cos(radV) * pathW;
    const deltaZ = Math.sin(radV) * pathW;

    // two candidate offset segments (side0 and side1)
    const pos0d0 = {x: pos02D.x + deltaX, y: 0, z: pos02D.z + deltaZ},
        pos0d1 = {x: pos02D.x - deltaX, y: 0, z: pos02D.z - deltaZ},
        pos1d0 = {x: pos12D.x + deltaX, y: 0, z: pos12D.z + deltaZ},
        pos1d1 = {x: pos12D.x - deltaX, y: 0, z: pos12D.z - deltaZ};

    // helper: count how many sampled points along segment [a,b] are inside polygon
    function sampleInsideCount(a: XYZCoordinates, b: XYZCoordinates, samples: number = 6) {
        let count = 0;
        for (let i = 0; i <= samples; i++) {
            const t = i / samples;
            const sx = a.x + (b.x - a.x) * t;
            const sz = a.z + (b.z - a.z) * t;
            if (CheckInsidePos(pos2DArr, {x: sx, z: sz})) count++;
        }
        return count;
    }

    const samples = 6; // increase for more robustness at cost of CPU
    const countSide0 = sampleInsideCount(pos0d0, pos1d0, samples);
    const countSide1 = sampleInsideCount(pos0d1, pos1d1, samples);

    let posPath2D;

    if (countSide0 > 0 || countSide1 > 0) {
        // choose the side with more inside samples
        posPath2D = countSide0 >= countSide1 ? [pos0d0, pos1d0] : [pos0d1, pos1d1];
    } else {
        // fallback: check endpoints + midpoints (handles very narrow or borderline cases)
        let f0 = 0, f1 = 0;
        if (CheckInsidePos(pos2DArr, pos0d0)) f0++;
        if (CheckInsidePos(pos2DArr, pos1d0)) f0++;
        if (CheckInsidePos(pos2DArr, pos0d1)) f1++;
        if (CheckInsidePos(pos2DArr, pos1d1)) f1++;

        const mid0 = {x: (pos0d0.x + pos1d0.x) / 2, z: (pos0d0.z + pos1d0.z) / 2};
        const mid1 = {x: (pos0d1.x + pos1d1.x) / 2, z: (pos0d1.z + pos1d1.z) / 2};
        if (CheckInsidePos(pos2DArr, mid0)) f0++;
        if (CheckInsidePos(pos2DArr, mid1)) f1++;

        if (f0 > 0 || f1 > 0) posPath2D = f0 >= f1 ? [pos0d0, pos1d0] : [pos0d1, pos1d1];
        else {
            // still ambiguous: don't pick a side (caller will skip drawing)
            posPath2D = undefined;
        }
    }

    // Ensure ordering matches original edge direction pos02D -> pos12D
    if (posPath2D && posPath2D.length === 2) {
        const d0 = Math.hypot(posPath2D[0].x - pos02D.x, posPath2D[0].z - pos02D.z);
        const d1 = Math.hypot(posPath2D[1].x - pos02D.x, posPath2D[1].z - pos02D.z);
        if (d1 < d0) {
            // endpoints reversed relative to original edge: swap
            const tmp = posPath2D[0];
            posPath2D[0] = posPath2D[1];
            posPath2D[1] = tmp;
        }
    }

    return {posPath2D, pos02D, pos12D};
}

export function GetHighestRidges(lines: Line[]): Line[] {
    // Only lines with points
    const ridgeLines = lines.filter(
        (l): l is Line & { points: Vertex3D[] } =>
            l.type === "RIDGE" && Array.isArray(l.points) && l.points.length >= 2
    );

    if (ridgeLines.length === 0) return [];

    ridgeLines.sort(
        (a, b) =>
            Math.max(b.points[0].y, b.points[1].y) - Math.max(a.points[0].y, a.points[1].y)
    );

    const highestRidge = ridgeLines[0];

    const ridges: Line[] = [];

    const getLineVector = (line: Line & { points: Vertex3D[] }) =>
        new THREE.Vector3(
            line.points[1].x - line.points[0].x,
            line.points[1].y - line.points[0].y,
            line.points[1].z - line.points[0].z
        );


    const ridgeDirection = getLineVector(highestRidge);

    for (const line of ridgeLines) {
        const dir = getLineVector(line);
        if (ridgeDirection.angleTo(dir) < Math.PI / 4) {
            ridges.push(line);
        }
    }

    return ridges;
}

export function ShouldAddSetbackToRoof(roofMesh: RoofMesh) {

    const setbackNotes = useViewerStore.getState().setbackNotes;

    const isDetachedExemptOrLowPitch = setbackNotes.some(note => note.toUpperCase().includes('DETACHED NONHABITABLE OR <9.5 PITCH EXEMPT'))

    if( (roofMesh.userData.pitchOverride < 9.5 && isDetachedExemptOrLowPitch)) return false

    if (setbackNotes[0] === 'NO SETBACKS') return false;

    if (roofMesh.size === undefined || isNaN(roofMesh.size)) return true;

    return roofMesh.size > 52;
}

export const hideGivenSetbackOrPathwayMesh = (mesh: SetbackLineMesh | PathwayMesh) => {
    mesh.visible = false;
}

export const toggleSetbackIfMarkedDetachedForGivenBuilding= (buildingId : number, isVisible :boolean) => {
    const sceneViewer : Viewer = useViewerStore.getState().sceneViewer;
    const targetBuildingId = Number(buildingId);
    sceneViewer.roofMeshArr.forEach((mesh : RoofMesh) => {
        if (Number(mesh.userData.buildingName) !== targetBuildingId) return;
        mesh.traverse((mesh: any) => {
            if (mesh.meshType === 'setback') {
                mesh.visible = !isVisible;
                mesh.isMarkedInactive = false;
            }
        })
    })
}

export const syncSetbackVisibilityForDetachedBuildings = () => {
    const detachedBuildingIds = useViewerStore.getState().detachedStructureBuildingIds;
    detachedBuildingIds.forEach(buildingId => {
        toggleSetbackIfMarkedDetachedForGivenBuilding(buildingId, true);
    })
}

function createAndAddPathwayMesh(
    params: {
        pos2DArr: Aurora2DCornersOfRoof[];
        type: string;
        width: number;
        posIdx: number;
        idxNext: number;
        active: boolean | undefined
        lineId: string;
        roofFaceId: string;
        shapeMesh: RoofMesh;
        self: Viewer;
    }
) {
    const {
        pos2DArr, type, width, posIdx, idxNext,
        active, lineId, roofFaceId, shapeMesh, self
    } = params;
    if (!Array.isArray(pos2DArr) || pos2DArr.length < 2) return;
    if (!pos2DArr[posIdx] || !pos2DArr[idxNext]) return;

    const { posPath2D, pos02D, pos12D } =
        GetPosPath2DForPathway(pos2DArr, type, width, posIdx, idxNext);

    if (!posPath2D || !pos02D || !pos12D) return;

    posPath2D.push(pos12D, pos02D);

    const shape = GetShapeOrPath(posPath2D);
    const geometry = new THREE.ExtrudeGeometry(shape, {
        depth: 0.1,
        bevelEnabled: true,
        bevelThickness: 0.1,
        bevelSize: 0,
        bevelSegments: 1,
    });

    const material = new THREE.MeshStandardMaterial({
        color: active ? 0x00ff00 : 0xff0000,
        transparent: true,
        opacity: 0.7,
    });

    const pathwayMesh = new THREE.Mesh(geometry, material) as PathwayMesh;
    pathwayMesh.pathwayId = lineId;
    pathwayMesh.pathWayRoofId = roofFaceId;
    pathwayMesh.pathwayPosArr = [...posPath2D];
    pathwayMesh.name = 'pathway';
    pathwayMesh.meshType = 'pathway';
    pathwayMesh.size = calculateMeshAreaInSquareFeet(pathwayMesh);

    pathwayMesh.material.polygonOffset = true;
    pathwayMesh.material.polygonOffsetFactor = -2;
    pathwayMesh.material.polygonOffsetUnits = 1;

    pos2DArr[posIdx].pathX = posPath2D[0].x;
    pos2DArr[idxNext].pathX = posPath2D[1].x;

    if (ifRoofDeactivated(shapeMesh.userData.roof_tag_id)) {
        hideGivenSetbackOrPathwayMesh(pathwayMesh);
    }

    self.meshArr.push(pathwayMesh);
    self.pathwayMeshArr.push(pathwayMesh);
    shapeMesh.add(pathwayMesh);
}


export function AddPathwayMesh(face: FaceItem, shapeMesh: RoofMesh, pos2DArr: Aurora2DCornersOfRoof[], self: Viewer) {

    const {posArr, lines, roofFaceId} = face;
    if (!Array.isArray(posArr) || posArr.length < 2) return;
    if (!Array.isArray(pos2DArr) || pos2DArr.length < 2) return;
    if (!Array.isArray(lines) || lines.length === 0) return;

    let setbackLineTypes = fixedSetbackEdges.length > 0 ? fixedSetbackEdges : ['RIDGE'];

    posArr.forEach((pos, posIdx) => {
        const idxNext = posIdx === posArr.length - 1 ? 0 : posIdx + 1;
        const posNext = posArr[idxNext];
        const points = [{...pos}, {...posNext}];
        const lineInfo = lines.find((line:Line) => checkSameLine(points, line.points));

        if (!lineInfo) return;

        let {pathway, setback, type, lineId, active, possiblePathway} = lineInfo;

        if (!lineId) {
            logger.error("Line ID is missing for line:", lineInfo);
            console.log("Line ID missing")
            return;
        }

        if (possiblePathway === true) pathway = true

        let effectiveSetback = fixedSetbackEdges.length > 0 ? fixedSetbackDimension[type] : setback;

        if (type === 'VALLEY') {
            pathway = true;
            effectiveSetback = fixedSetbackEdges.length > 0 ? fixedSetbackDimension['VALLEY'] : setback;
        }


        if (pathway && type !== 'EAVE') {
            createAndAddPathwayMesh({
                pos2DArr,
                type,
                width: dePathW,
                posIdx,
                idxNext,
                active,
                lineId,
                roofFaceId,
                shapeMesh,
                self,
            });
        }

        if (effectiveSetback && setbackLineTypes.includes(type)) {

            const setbackWidth = effectiveSetback ? effectiveSetback * inch2m : dePathW;
            const {posPath2D, pos02D, pos12D} = GetPosPath2D(pos2DArr, type, setbackWidth, posIdx, idxNext);

            if (!posPath2D) return;
            if (!ShouldAddSetbackToRoof(shapeMesh)) return;

            const linePosArr: XYZCoordinates[] = [];
            const linePositions: number[] = [];

            posPath2D.forEach((pos) => {
                linePosArr.push(new THREE.Vector3(pos.x, pos.z, pos.y));
                linePositions.push(pos.x, pos.z, pos.y);
            });

            let pos18Info: any;
            shapeMesh.lines.forEach((line: Line) => {
                if (line.lineId !== lineInfo.lineId) return;
                line.setbackW = setback;
                line.setbackInfo = {pos36: [...linePosArr], pos18: []};
                pos18Info = GetPosPath2D(pos2DArr, type, 18 * inch2m, posIdx, idxNext);
                pos18Info.posPath2D?.forEach((pos: XYZCoordinates) => {
                    if(line.setbackInfo) line.setbackInfo.pos18.push(new THREE.Vector3(pos.x, pos.z, pos.y));
                });
            });


            const lineGeo = new LineGeometry();
            lineGeo.setPositions(linePositions);

            const lineMat = new LineMaterial({
                color: 0x00ff00,
                linewidth: 6,
                resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
            });

            const setbackMesh = new Line2(lineGeo, lineMat) as SetbackLineMesh
            setbackMesh.computeLineDistances();
            setbackMesh.pathWayRoofId = roofFaceId;
            setbackMesh.pathwayPosArr = [...posPath2D, pos12D, pos02D];
            setbackMesh.pathwayPosArr18 = pos18Info.posPath2D ? [...pos18Info.posPath2D, pos18Info.pos12D, pos18Info.pos02D] : [];
            pos2DArr[posIdx].backZ = posPath2D[0].z;
            pos2DArr[idxNext].backZ = posPath2D[1].z;
            setbackMesh.name = 'setback';
            setbackMesh.meshType = 'setback';
            setbackMesh.setbackId = lineId;
            if (fixedSetbackDimension['RIDGE'] === 18) {
                setbackMesh.userData.use18 = true;
            }
            if (ifRoofDeactivated(shapeMesh.userData.roof_tag_id)) {
                hideGivenSetbackOrPathwayMesh(setbackMesh);
            }
            shapeMesh.add(setbackMesh);

        }


        if (shapeMesh.oriAng.tilt < 5) {
            createAndAddPathwayMesh({
                pos2DArr,
                type: 'VALLEY',
                width: 6 * ft2m,
                posIdx,
                idxNext,
                active,
                lineId,
                roofFaceId,
                shapeMesh,
                self,
            });
        }

    });
}


export function SetTogglePathwayValue(self: Viewer, selPathwayId: string) {
    const {roofs, roofGroup} = self;
    roofs[0].faces.forEach((face: FaceItem) => {
        face.lines.forEach((line: Line) => {
            if (line.lineId === selPathwayId) {
                let newStatus = !line.active
                line.active = !line.active;
                roofGroup.traverse((child: any) => {
                    if (child.pathwayId === selPathwayId) {
                        child.pathActive = newStatus;
                        SetMaxFillForRoofs(self, face.roofFaceId);
                    }
                })
            }
        });
    });
    SetPathwayActiveModule(self, selPathwayId);
}

/** When `updateMaxFillModuleCounts` is false, skips `SetMaxFillForRoofs` (avoids eager autofill/count solver). */
export type SetPathwayValueOptions = {
    updateMaxFillModuleCounts?: boolean;
};

export function SetPathwayValue(
    self: Viewer,
    selPathwayId: string,
    value: boolean,
    options?: SetPathwayValueOptions
) {
    const { roofs, roofGroup } = self;

    if (!roofGroup || !roofs?.length) return;

    const { updateMaxFillModuleCounts = true } = options ?? {};

    let affectedRoofFaceId: string | number | null = null;

    roofs[0].faces.forEach((face:FaceItem) => {
        face.lines.forEach(line => {
            if (line.lineId === selPathwayId) {
                line.active = value;
                affectedRoofFaceId = face.roofFaceId;
            }
        });
    });

    if (!affectedRoofFaceId) return;

    roofGroup.traverse((child:any) => {
        if (child.pathwayId === selPathwayId) {
            child.pathActive = value;
        }
    });

    if (updateMaxFillModuleCounts) {
        SetMaxFillForRoofs(self, affectedRoofFaceId);
    }
    SetPathwayActiveModule(self, selPathwayId);
}

export function SetPathwayActiveModule(self: Viewer, selPathwayId: string) {
    const { roofs, meshArr, moduleMeshArr, roofGroup } = self;

    let pathwayStatus = false;
    let pathwayMesh: PathwayMesh | null = null;

    roofs[0].faces.forEach( (face: FaceItem) => {
        face.lines.forEach(line => {
            if (line.lineId === selPathwayId) {
                pathwayStatus = !!line.active;
            }
        });
    });

    // find the actual pathway polygon
    roofGroup.traverse((child: any) => {
        if (child.meshType === 'pathway' && child.pathwayId === selPathwayId) {
            pathwayMesh = child;
        }
    });

    meshArr.forEach(mesh => {
        if (mesh.pathwayId === selPathwayId) {
            mesh.material.color.setHex(pathwayStatus ? 0x00ff00 : 0xff0000);
            mesh.scale.z = pathwayStatus ? 1.5 : 1;
            mesh.material.opacity = 0.7;
        }
    });

    if (!pathwayStatus || !pathwayMesh) return;

    const modulesToDelete: ModuleMesh[] = [];

    moduleMeshArr.forEach(module => {
        const {overPathIds} = module;
        if (!overPathIds || !overPathIds.includes(selPathwayId)) return;

        if (pathwayStatus) {
            modulesToDelete.push(module);
        }

        module.material.needsUpdate = true;
        module.pathwayOver = pathwayStatus;
    });

    for (const module of modulesToDelete) {
        const selRoof = self.roofMeshArr.find(r => r.roofFaceId === module.roofId);
        DeleteModule([module], selRoof, self.moduleMeshArr, self, true);
    }
}

/**
 * Pathway meshes use green (active) / red (inactive) materials. Some code paths update
 * `pathActive` (and `face.lines[].active`) without going through {@link SetPathwayActiveModule},
 * which leaves colors stale — especially during AI layout-variant sweeps. This reapplies
 * material color and scale from the current `pathActive` flag on every pathway mesh.
 */
export function syncPathwayMeshColorsFromPathActive(self: Viewer | null | undefined) {
    if (!self) return;

    const apply = (child: any) => {
        if (child?.meshType !== 'pathway' || !child.pathwayId || !child.material?.color) return;
        const active = !!child.pathActive;
        child.material.color.setHex(active ? 0x00ff00 : 0xff0000);
        child.scale.z = active ? 1.5 : 1;
        if (typeof child.material.opacity === 'number') {
            child.material.opacity = 0.7;
        }
        child.material.needsUpdate = true;
    };

    if (self.roofGroup) {
        self.roofGroup.traverse(apply);
    }
    (self.meshArr || []).forEach(apply);
}


export function UpdateSetbackDepth(self: Viewer | null | undefined, type?: string, selDir?: string) {

    if (!self) return;

    const isSetbackOverride = useViewerStore.getState().isSetbackOverride

    if (isSetbackOverride) return;

    if (type === 'modeCount' && selDir === 'land') return;
    const {buildingArr, roofMeshArr} = self;

    buildingArr.forEach(building => {
        building.modeCount = 0;
    });

    roofMeshArr.forEach(roofMesh => {
        if (isGroundMount(roofMesh)) return;
        const modules = getRoofModuleMeshes(roofMesh);
        const selBuilding = buildingArr.find(building => building.name === roofMesh.userData.buildingName);
        if (selBuilding) selBuilding.modeCount += modules.length;
    });

    buildingArr.forEach(building => {
        const {modeCount, modeCount33} = building;
        const mode33Over = modeCount >= modeCount33;
        if (building.mode33Over !== undefined && building.mode33Over !== mode33Over) {
        }
        building.mode33Over = mode33Over;
    });

    roofMeshArr.forEach(roofMesh => {
        const {lines, children} = roofMesh;
        const selBuilding = buildingArr.find(building => {
            return building.name === roofMesh.userData.buildingName;
        });
        if (!selBuilding || !lines) return;

        lines.forEach(line => {
            if (!line.setback || !line.setbackInfo) return;

            //update only if setback line is on ridge
            if (line.type !== 'RIDGE') return;

            //fixed and is type of ridge
            if (isFixedRidgeSetback) return;


            const selSetbackMesh = children.find(
                (c): c is SetbackLineMesh => (c as SetbackLineMesh).setbackId === line.lineId
            );
            if (!selSetbackMesh) return;


            if (!selSetbackMesh) return;
            const {pos36, pos18} = line.setbackInfo,
                selSetbackPos = selBuilding.mode33Over ? pos36 : pos18;
            if (!selSetbackMesh || !selSetbackPos || selSetbackPos.length === 0) return;
            selSetbackMesh.userData.use18 = !selBuilding.mode33Over;

            // Convert selSetbackPos to Float32Array for LineGeometry
            const setbackPositions: number[] = [];
            selSetbackPos.forEach((point: XYZCoordinates) => {
                setbackPositions.push(point.x, point.y, point.z);
            });

            if (setbackPositions.length > 0) {
                const newLineGeo = new LineGeometry();
                newLineGeo.setPositions(setbackPositions);
                selSetbackMesh.geometry.dispose();
                selSetbackMesh.geometry = newLineGeo;
            }

        });
    });
}

export const OverrideSetbackDepthTo36 = (self: Viewer) => {
    const {buildingArr, roofMeshArr} = self;

    buildingArr.forEach(b => {
        b.mode33Over = true;
    });

    roofMeshArr.forEach(roofMesh => {
        const {lines, children} = roofMesh;

        lines.forEach(line => {
            if (!line.setback || !line.setbackInfo) return;
            if (line.type !== 'RIDGE') return;
            if (isFixedRidgeSetback) return;

            const selSetbackMesh = children.find(
                (c): c is SetbackLineMesh => (c as SetbackLineMesh).setbackId === line.lineId
            );
            if (!selSetbackMesh) return;

            const {pos36} = line.setbackInfo;
            if (!pos36 || pos36.length === 0) return;

            selSetbackMesh.userData.use18 = false;       // always false because we force 36 in

            const arr: number[] = [];
            pos36.forEach(p => arr.push(p.x, p.y, p.z));

            if (arr.length) {
                const geo = new LineGeometry();
                geo.setPositions(arr);
                selSetbackMesh.geometry.dispose();
                selSetbackMesh.geometry = geo;
            }
        });
    });
};

export const checkAndSetSetbackOverrideFlag = (designDataString: string) => {
    const isSetbackOverrideFlag = JSON.parse(designDataString).isSetbackOverrideEnabled ?? false
    useViewerStore.setState({isSetbackOverride: isSetbackOverrideFlag})
}

const flagIfModulesOverSetbackLine = (module: ModuleMesh, setbackArr: any[]) => {
    const verArr = module.verInfo.verArr
    const lineArr = [verArr[0], verArr[1], verArr[3], verArr[2]]

    for (const pos of verArr) {
        for (const setback of setbackArr) {
            if (CheckInsidePos(setback.posArr, pos)) return ModulePlacementStatusSetback;
        }
    }

    for (const setback of setbackArr) {
        for (const pos of setback.posArr) {
            if (CheckInsidePos(lineArr, pos)) return ModulePlacementStatusSetback;
        }
    }

}

export const checkIfAnyRoofHasModulesOverSetbackLines = (): boolean => {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    const detachedStructureBuildingIds = useViewerStore.getState().detachedStructureBuildingIds;
    return sceneViewer.roofMeshArr.some((roofMesh: RoofMesh) => {
        //exempt for detached structure
        const buildingId = Number(roofMesh.userData.buildingName);

        if (detachedStructureBuildingIds.includes(buildingId)) return false;

        const modules = getRoofModuleMeshes(roofMesh);
        const {setbackArr} = GetSetPathArr(sceneViewer.roofGroup, roofMesh.roofFaceId);

        return modules.some(module =>
            flagIfModulesOverSetbackLine(module, setbackArr) === ModulePlacementStatusSetback
        );
    });
};